import { useCallback, useEffect, useState } from 'react';
import { AppealError, AppealRoom, AppealSession, getRoom, onLocalRoomUpdate, subscribeToRoom } from '../services/appealsService';

/** Loads an appeal room and reloads it whenever anyone changes it. */
export const useAppealRoom = (session: AppealSession | null) => {
  const [room, setRoom] = useState<AppealRoom | null>(null);
  const [error, setError] = useState('');
  /** The room is gone or the session is no longer valid: the user has to enter a code again. */
  const [expired, setExpired] = useState(false);
  const [loading, setLoading] = useState(Boolean(session));

  const reload = useCallback(async () => {
    if (!session) return;
    try {
      setRoom(await getRoom(session));
      setError('');
    } catch (e) {
      const status = e instanceof AppealError ? e.status : undefined;
      setError(e instanceof Error ? e.message : 'Otaq yüklənmədi.');
      if (status === 401 || status === 404) {
        setExpired(true);
        setRoom(null);
      }
    } finally {
      setLoading(false);
    }
  }, [session]);

  useEffect(() => {
    setRoom(null);
    setExpired(false);
    if (!session) return;
    setLoading(true);
    reload();
    const unsubscribeLocal = onLocalRoomUpdate(next => {
      if (next.id === session.roomId) setRoom(next);
    });
    const unsubscribe = subscribeToRoom(session, reload);
    return () => {
      unsubscribeLocal();
      unsubscribe();
    };
  }, [session, reload]);

  return { room, error, expired, loading, reload };
};
